import { useState, useEffect } from 'react';
import './WorkflowSection.css';

const emailLines = [
  'Subject: Loved your MVP launch',
  '',
  'Hi Sarah,',
  '',
  'Saw we have a mutual connection (David L.) on',
  'LinkedIn. I just watched your MVP launch video—the',
  'haptic feedback loop is incredible.',
  '',
  "I'm an investor at Venture Partners, and we specialize",
  'in hardware-first founders. Would love to chat.'
];

const crmEntries = [
  {
    company: 'Aether',
    founder: 'Sarah J.',
    signal: 'Viral Twitter',
    status: 'Parsing...'
  },
  {
    company: 'CampusSync',
    founder: 'Mike T.',
    signal: 'Thesis Match',
    status: 'Deduping...'
  },
  {
    company: 'FlowState',
    founder: 'Alex K.',
    signal: 'YC Demo Day',
    status: 'Enriching...'
  }
];

const WorkflowSection = () => {
  const [activeTab, setActiveTab] = useState('outbound');
  const [typedText, setTypedText] = useState('');
  const [visibleCrmEntries, setVisibleCrmEntries] = useState([]);
  const [radarBlips, setRadarBlips] = useState([]);

  useEffect(() => {
    if (activeTab === 'outbound') {
      // Reset typing
      setTypedText('');

      let lineIndex = 0;
      let charIndex = 0;
      let currentText = '';

      const typeChar = () => {
        if (lineIndex < emailLines.length) {
          const line = emailLines[lineIndex];

          if (charIndex < line.length) {
            currentText += line[charIndex];
            setTypedText(currentText);
            charIndex++;
            setTimeout(typeChar, 30); // Type each character
          } else {
            // Move to next line
            currentText += '\n';
            setTypedText(currentText);
            lineIndex++;
            charIndex = 0;

            if (lineIndex < emailLines.length) {
              setTimeout(typeChar, 100); // Brief pause between lines
            }
          }
        }
      };

      // Start typing after a brief delay
      setTimeout(typeChar, 500);
    } else if (activeTab === 'crm') {
      // Reset CRM entries
      setVisibleCrmEntries([]);

      // Show entries one by one
      const showEntry = (index) => {
        if (index < crmEntries.length) {
          setTimeout(() => {
            setVisibleCrmEntries((prev) => [...prev, index]);
            showEntry(index + 1);
          }, 1200); // 1200ms delay between entries (slower)
        }
      };

      setTimeout(() => showEntry(0), 800);
    } else if (activeTab === 'lead') {
      // Reset radar blips
      setRadarBlips([]);

      let blipId = 0;
      const createBlip = () => {
        const newBlip = {
          id: blipId++,
          angle: Math.random() * 360,
          distance: 15 + Math.random() * 30, // 15-45% from center (safe zone)
          label: ['Twitter Post', 'Product Hunt', 'GitHub Trending', 'LinkedIn Post', 'Collge Blog', 'Research Article', 'Cohort Announcement'][Math.floor(Math.random() * 5)]
        };

        // Add the blip (replace any existing blips to ensure only one at a time)
        setRadarBlips([{ ...newBlip, state: 'fadein' }]);

        // After fade in (0.3s), set to hold state
        setTimeout(() => {
          setRadarBlips((prev) => prev.map(b => b.id === newBlip.id ? { ...b, state: 'hold' } : b));
        }, 300);

        // After hold (2s), set to fadeout state
        setTimeout(() => {
          setRadarBlips((prev) => prev.map(b => b.id === newBlip.id ? { ...b, state: 'fadeout' } : b));
        }, 2300);

        // After fadeout (0.3s), remove the blip and schedule next
        setTimeout(() => {
          setRadarBlips([]);
          // Schedule next blip after 1s gap
          setTimeout(createBlip, 1000);
        }, 2600);
      };

      // Start creating blips
      setTimeout(createBlip, 500);
    }
  }, [activeTab]);

  return (
    <section className="workflow-section">
      <h2 className="workflow-title">
        The entire workflow, <span className="workflow-title-accent">solved.</span>
      </h2>

      <div className="workflow-container">
        <div className="workflow-tabs">
          <button
            className={`workflow-tab ${activeTab === 'lead' ? 'active' : ''}`}
            onClick={() => setActiveTab('lead')}
          >
            <h3 className="workflow-tab-title">Lead Generation</h3>
            <p className="workflow-tab-subtitle">Multi-source signal triangulation.</p>
          </button>

          <button
            className={`workflow-tab ${activeTab === 'crm' ? 'active' : ''}`}
            onClick={() => setActiveTab('crm')}
          >
            <h3 className="workflow-tab-title">CRM Integration</h3>
            <p className="workflow-tab-subtitle">Auto-deduping and enrichment.</p>
          </button>

          <button
            className={`workflow-tab ${activeTab === 'outbound' ? 'active' : ''}`}
            onClick={() => setActiveTab('outbound')}
          >
            <h3 className="workflow-tab-title">Outbound Automation</h3>
            <p className="workflow-tab-subtitle">Hyper-personalized drafting.</p>
          </button>
        </div>

        <div className="workflow-content">
          {activeTab === 'outbound' && (
            <div className="workflow-email-preview">
              <div className="workflow-email-header">
                <div className="workflow-email-avatar">A</div>
                <div className="workflow-email-meta">
                  <div className="workflow-email-to">To: Sarah Jenkins</div>
                  <div className="workflow-email-subject">Subject: Loved your MVP launch</div>
                </div>
              </div>
              <div className="workflow-email-body">
                <pre className="workflow-email-text">{typedText}<span className="workflow-cursor">|</span></pre>
              </div>
            </div>
          )}

          {activeTab === 'lead' && (
            <div className="workflow-radar">
              <div className="workflow-radar-spinner"></div>
              <div className="workflow-radar-center"></div>
              {radarBlips.map((blip) => {
                const x = Math.cos((blip.angle - 90) * Math.PI / 180) * blip.distance;
                const y = Math.sin((blip.angle - 90) * Math.PI / 180) * blip.distance;
                return (
                  <div
                    key={blip.id}
                    className={`workflow-radar-blip workflow-radar-blip-${blip.state}`}
                    style={{
                      left: `calc(50% + ${x}%)`,
                      top: `calc(50% + ${y}%)`
                    }}
                  >
                    <div className="workflow-radar-blip-dot"></div>
                    <div className="workflow-radar-blip-label">{blip.label}</div>
                  </div>
                );
              })}
            </div>
          )}

          {activeTab === 'crm' && (
            <div className="workflow-crm-table">
              <div className="workflow-crm-header">
                <div className="workflow-crm-cell">COMPANY</div>
                <div className="workflow-crm-cell">FOUNDER</div>
                <div className="workflow-crm-cell">SIGNAL</div>
                <div className="workflow-crm-cell">STATUS</div>
              </div>
              {crmEntries.map((entry, index) => {
                const isVisible = visibleCrmEntries.includes(index);
                return (
                  <div key={index} className={`workflow-crm-row ${isVisible ? 'visible' : 'placeholder'}`}>
                    {isVisible ? (
                      <>
                        <div className="workflow-crm-cell">{entry.company}</div>
                        <div className="workflow-crm-cell">{entry.founder}</div>
                        <div className="workflow-crm-cell">
                          <span className="workflow-crm-signal">{entry.signal}</span>
                        </div>
                        <div className="workflow-crm-cell">
                          <span className="workflow-crm-status">{entry.status}</span>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="workflow-crm-cell"><span className="workflow-crm-skeleton"></span></div>
                        <div className="workflow-crm-cell"><span className="workflow-crm-skeleton"></span></div>
                        <div className="workflow-crm-cell"><span className="workflow-crm-skeleton"></span></div>
                        <div className="workflow-crm-cell"><span className="workflow-crm-skeleton"></span></div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default WorkflowSection;
